import { useState } from 'react';
import { Eye, EyeOff, Lock, Loader2, MailCheck, KeyRound, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { auth } from '@/lib/api';

type Mode = 'login' | 'register' | 'verify' | 'forgot' | 'reset';

interface LoginProps {
  onLogin: (email: string, password: string) => Promise<string | null>;
}

export default function Login({ onLogin }: LoginProps) {
  const [mode, setMode] = useState<Mode>('login');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const switchMode = (m: Mode, msg = '') => {
    setMode(m);
    setError('');
    setNotice(msg);
    setOtp('');
    setPassword('');
  };

  const run = async (fn: () => Promise<{ status: string; message?: string }>, onOk: () => void) => {
    setIsLoading(true);
    setError('');
    const res = await fn();
    setIsLoading(false);
    if (res.status === 'success') onOk();
    else setError(res.message || 'Something went wrong.');
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    const err = await onLogin(email, password);
    if (err) {
      setError(err);
      setIsLoading(false);
    }
  };

  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    run(
      () => auth.register(email, name, password),
      () => switchMode('verify', 'Verification code sent to your email.')
    );
  };

  const handleVerify = (e: React.FormEvent) => {
    e.preventDefault();
    run(
      () => auth.verify(email, otp),
      () => switchMode('login', 'Account verified — sign in with your password.')
    );
  };

  const handleForgot = (e: React.FormEvent) => {
    e.preventDefault();
    run(
      () => auth.forgot(email),
      () => switchMode('reset', 'Reset code sent to your email.')
    );
  };

  const handleReset = (e: React.FormEvent) => {
    e.preventDefault();
    run(
      () => auth.reset(email, otp, password),
      () => switchMode('login', 'Password reset — sign in with the new password.')
    );
  };

  const titles: Record<Mode, { icon: React.ReactNode; title: string; desc: string }> = {
    login: {
      icon: <Lock className="size-4 text-foreground" />,
      title: 'Welcome back',
      desc: 'Enter your email and password',
    },
    register: {
      icon: <UserPlus className="size-4 text-foreground" />,
      title: 'Create account',
      desc: 'Only team-member emails can register',
    },
    verify: {
      icon: <MailCheck className="size-4 text-foreground" />,
      title: 'Check your email',
      desc: `We sent a 6-digit code to ${email}`,
    },
    forgot: {
      icon: <KeyRound className="size-4 text-foreground" />,
      title: 'Reset password',
      desc: "We'll email you a reset code",
    },
    reset: {
      icon: <KeyRound className="size-4 text-foreground" />,
      title: 'Set new password',
      desc: `Enter the code sent to ${email}`,
    },
  };
  const t = titles[mode];

  const passwordField = (
    <div className="flex flex-col gap-2">
      <Label htmlFor="password">
        {mode === 'reset' ? 'New password' : 'Password'}
      </Label>
      <div className="relative">
        <Input
          id="password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setError('');
          }}
          placeholder={mode === 'register' || mode === 'reset' ? 'Min. 8 characters' : 'Enter password'}
          aria-invalid={!!error}
          className={cn('pr-10')}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
        >
          {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    </div>
  );

  const emailField = (
    <div className="flex flex-col gap-2">
      <Label htmlFor="email">Email</Label>
      <Input
        id="email"
        type="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          setError('');
        }}
        placeholder="you@hashtaginc.co.in"
        autoComplete="email"
      />
    </div>
  );

  const otpField = (
    <div className="flex flex-col gap-2">
      <Label htmlFor="otp">Verification code</Label>
      <Input
        id="otp"
        inputMode="numeric"
        maxLength={6}
        value={otp}
        onChange={(e) => {
          setOtp(e.target.value.replace(/\D/g, ''));
          setError('');
        }}
        placeholder="6-digit code"
        className="tracking-[0.5em] text-center font-mono"
      />
    </div>
  );

  return (
    <div className="min-h-svh bg-muted/40 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img
              src="/assets/icons/hashtag_logo.svg"
              alt="Hashtag Inc."
              className="h-12 w-12 object-contain"
            />
          </div>
          <h1 className="text-2xl font-semibold text-foreground mb-1">LeadFlow</h1>
          <p className="text-muted-foreground text-sm">Hashtag Inc. Admin Dashboard</p>
        </div>

        <Card className="shadow-sm">
          <CardHeader className="gap-1.5">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-lg bg-secondary">
                {t.icon}
              </div>
              <div>
                <CardTitle>{t.title}</CardTitle>
                <CardDescription>{t.desc}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {notice && (
              <p className="text-emerald-600 dark:text-emerald-400 text-xs mb-3">{notice}</p>
            )}
            {error && (
              <p className="text-destructive text-xs mb-3" role="alert">{error}</p>
            )}

            {mode === 'login' && (
              <form onSubmit={handleLogin} className="flex flex-col gap-4">
                {emailField}
                {passwordField}
                <Button type="submit" disabled={isLoading || !email || !password} className="w-full">
                  {isLoading && <Loader2 className="animate-spin" />}
                  {isLoading ? 'Signing in…' : 'Sign In'}
                </Button>
                <div className="flex items-center justify-between text-xs">
                  <button type="button" onClick={() => switchMode('forgot')}
                    className="text-muted-foreground hover:text-foreground transition-colors">
                    Forgot password?
                  </button>
                  <button type="button" onClick={() => switchMode('register')}
                    className="text-muted-foreground hover:text-foreground transition-colors">
                    Create account
                  </button>
                </div>
              </form>
            )}

            {mode === 'register' && (
              <form onSubmit={handleRegister} className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </div>
                {emailField}
                {passwordField}
                <Button type="submit" disabled={isLoading || !email || !password} className="w-full">
                  {isLoading && <Loader2 className="animate-spin" />}
                  {isLoading ? 'Sending code…' : 'Send verification code'}
                </Button>
                <button type="button" onClick={() => switchMode('login')}
                  className="text-muted-foreground hover:text-foreground text-xs transition-colors self-center">
                  Back to sign in
                </button>
              </form>
            )}

            {mode === 'verify' && (
              <form onSubmit={handleVerify} className="flex flex-col gap-4">
                {otpField}
                <Button type="submit" disabled={isLoading || otp.length !== 6} className="w-full">
                  {isLoading && <Loader2 className="animate-spin" />}
                  {isLoading ? 'Verifying…' : 'Verify & create account'}
                </Button>
                <button type="button" onClick={() => switchMode('login')}
                  className="text-muted-foreground hover:text-foreground text-xs transition-colors self-center">
                  Back to sign in
                </button>
              </form>
            )}

            {mode === 'forgot' && (
              <form onSubmit={handleForgot} className="flex flex-col gap-4">
                {emailField}
                <Button type="submit" disabled={isLoading || !email} className="w-full">
                  {isLoading && <Loader2 className="animate-spin" />}
                  {isLoading ? 'Sending…' : 'Send reset code'}
                </Button>
                <button type="button" onClick={() => switchMode('login')}
                  className="text-muted-foreground hover:text-foreground text-xs transition-colors self-center">
                  Back to sign in
                </button>
              </form>
            )}

            {mode === 'reset' && (
              <form onSubmit={handleReset} className="flex flex-col gap-4">
                {otpField}
                {passwordField}
                <Button type="submit" disabled={isLoading || otp.length !== 6 || password.length < 8} className="w-full">
                  {isLoading && <Loader2 className="animate-spin" />}
                  {isLoading ? 'Resetting…' : 'Reset password'}
                </Button>
                <button type="button" onClick={() => switchMode('login')}
                  className="text-muted-foreground hover:text-foreground text-xs transition-colors self-center">
                  Back to sign in
                </button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-muted-foreground text-xs mt-6">
          © {new Date().getFullYear()} Hashtag Inc. All rights reserved.
        </p>
      </div>
    </div>
  );
}
